import {
  Button,
  Grid,
  IconButton,
  makeStyles,
  Typography,
} from "@material-ui/core"
import { ContentCard, ViraText } from "@ksana/vira-ui"
import clsx from "clsx"
import React, { FC, useState } from "react"
import { BounceLoader } from "react-spinners"
import { useTrends } from "../../hooks/useTrends"
import { colors } from "../../utils/theme"
import ChartAccordion from "../molecules/ChartAccordion"
import dayjs, { Dayjs } from "dayjs"
import ActivityChartHelpIcon from "../molecules/ActivityChartHelpIcon"
import WeeklyAverages from "../molecules/WeeklyAverages"
import ActivitiesChart from "../molecules/ActivitiesChart"
import SleepChart from "../molecules/SleepChart"
import SleepChartHelpIcon from "../molecules/SleepChartHelpIcon"
import { ClientFragment } from "../../generated/graphql"
import MorningnessChart from "../molecules/MorningnessChart"
import MorningnessChartHelpIcon from "../molecules/MorningnessChartHelpIcon"
import LanguageChart from "../molecules/LanguageChart"
import LanguageChartHelpIcon from "../molecules/LanguageChartHelpIcon"
import SocialMediaChartHelpIcon from "../molecules/SocialMediaChartHelpIcon"
import TimeAtHomeChartHelpIcon from "../molecules/TimeAtHomeHelpIcon"
import SocialMediaChart from "../molecules/SocialMediaChart"
import TimeAtHomeChart from "../molecules/TimeAtHomeChart"
import { generatePdf } from "../../utils/generatePdf"
import CravingsChart from "./charts/CravingsChart"
import EnjoymentChart from "./charts/EnjoymentChart"
import MyPlacesChart from "./charts/MyPlacesChart"
import { useMyPlacesTrend } from "../../hooks/useMyPlacesTrend"
import ChevronLeftLargeIcon from "../atoms/ChevronLeftLargeIcon"
import ChevronRightIcon from "../atoms/ChevronRightIcon"

const useStyles = makeStyles((theme) => ({
  trends: {
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
  },
  charts: {
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
  },
  assessmentsCharts: {
    width: "100%",
    display: "flex",
    flexDirection: "row",
    gap: 24,
    alignItems: "flex-start",
    flexWrap: "wrap",
  },
  assessmentsContent: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    gap: 24,
  },
  assessmentsHeader: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: 16,
  },
  assessmentsNavigation: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  assessmentsWeekLabel: {
    color: colors.neutral.denim,
    fontWeight: 400,
    minWidth: 120,
    textAlign: "center",
  },
  assessmentsChevron: {
    border: `1px solid ${colors.neutral.clay}`,
    borderRadius: 4,
    padding: 8,
    backgroundColor: colors.neutral.white,
    "&.Mui-disabled": {
      opacity: 0.35,
      borderColor: colors.neutral.clay,
      backgroundColor: colors.neutral.white,
    },
    "&.Mui-disabled svg": {
      opacity: 0.35,
    },
  },
  returnToCurrentWeek: {
    textTransform: "none",
  },
  barChartContainer: {
    maxWidth: "450px",
    width: "100%",
    minWidth: 0,
  },
  myPlacesChartContainer: {
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
  },
  chartContainer: {
    border: `1px solid ${colors.neutral.clay}`,
    borderRadius: 4,
    padding: 24,
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    boxSizing: "border-box",
    overflow: "hidden",
    [theme.breakpoints.down("sm")]: {
      padding: 16,
    },
  },
  /** Client Detail: nested chart panels use the same card border/radius. */
  chartContainerClientDetail: {
    border: "1px solid #E4E7EF",
    borderRadius: 10,
    padding: 16,
    backgroundColor: colors.neutral.white,
  },
  chartTitleClientDetail: {
    fontFamily: "Poppins, sans-serif",
    fontWeight: 600,
    fontSize: 15,
    letterSpacing: "-0.15px",
    color: "#1B2A4A",
  },
  noDataContainerClientDetail: {
    border: "1px solid #E4E7EF",
    borderRadius: 10,
  },
  topChart: {
    marginBottom: 16,
  },
  leftChart: {
    marginRight: 0,
    marginBottom: 16,
    [theme.breakpoints.up("md")]: {
      marginRight: 32,
      marginBottom: 0,
    },
  },
  chartTitle: {
    color: colors.neutral.denim,
    fontWeight: 600,
    fontSize: 15,
    fontFamily: "Poppins, sans-serif",
    marginRight: 4,
  },
  walkingCircle: {
    height: 12,
    width: 12,
    backgroundColor: "#FFE4CC",
    border: "2px solid #FFAC62",
    borderRadius: "50%",
  },
  exerciseCircle: {
    height: 12,
    width: 12,
    backgroundColor: "#CCF6FF",
    border: "2px solid #0BBBE2",
    borderRadius: "50%",
  },
  stationaryCircle: {
    height: 12,
    width: 12,
    backgroundColor: "#FFB2B2",
    border: "2px solid #FF5858",
    borderRadius: "50%",
  },
  drivingCircle: {
    height: 12,
    width: 12,
    backgroundColor: "#E0FFE5",
    border: "2px solid #92DB9D",
    borderRadius: "50%",
  },
  tooltip: {
    color: colors.neutral.white,
  },
  titleHelp: {
    display: "flex",
    alignItems: "center",
    minWidth: 0,
    flexWrap: "wrap",
  },
  titleDate: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: 8,
    minWidth: 0,
  },
  date: {
    fontFamily: "Lato",
    fontWeight: 400,
    fontSize: 12,
    letterSpacing: 0.3,
    textAlign: "right",
    fontStyle: "normal",
    padding: 0,
    margin: 0,
    color: colors.neutral.asphalt,
  },
  noData: {
    color: colors.neutral.asphalt,
    fontWeight: 400,
    fontSize: 14,
  },
  noDataContainer: {
    border: `1px solid ${colors.neutral.clay}`,
    height: 241,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginTop: 16,
    borderRadius: 10,
    paddingLeft: 48,
    paddingRight: 48,
    [theme.breakpoints.down("sm")]: {
      paddingLeft: 16,
      paddingRight: 16,
      height: "auto",
      minHeight: 160,
    },
  },
  morningnessChartContainer: {
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
  },
}))

const getAssessmentWeekLabel = (weekOffset: number) => {
  if (weekOffset === 0) {
    return "This Week"
  }

  if (weekOffset === 1) {
    return "Last Week"
  }

  return `${weekOffset} Weeks Ago`
}

const getEarliestAssessmentDate = (
  moodMeasurements: ClientFragment["mood_measurements"]
): Dayjs | null => {
  return moodMeasurements.reduce<Dayjs | null>((earliestDate, measurement) => {
    const measurementDate =
      measurement.client_created_on ?? measurement.created_on

    if (!measurementDate) {
      return earliestDate
    }

    const usableDate = dayjs(measurementDate).startOf("day").subtract(1, "day")

    if (!earliestDate || usableDate.isBefore(earliestDate)) {
      return usableDate
    }

    return earliestDate
  }, null)
}

/** Which Trends accordion groups to render. Client Detail Sleep/Activity/Language tabs pass a pane. */
export type TrendsPane = "all" | "sleep" | "activity" | "language"

const Trends: FC<{
  client: ClientFragment
  /** Defaults to full Trends (legacy tab). Loop nav uses sleep | activity | language. */
  pane?: TrendsPane
}> = ({ client, pane = "all" }) => {
  const classes = useStyles()
  const {
    trends,
    activity,
    categories,
    sleep,
    morningness,
    language,
    socialMedia,
    timeAtHome,
  } = useTrends(client)
  /** Client Detail Sleep / Activity / Language tabs — ViraCard-aligned chrome. */
  const isClientDetailPane = pane !== "all"
  const accordionVariant = isClientDetailPane ? "clientDetail" : "default"
  const chartBox = (...extra: Array<string | false | undefined>) =>
    clsx(
      classes.chartContainer,
      isClientDetailPane && classes.chartContainerClientDetail,
      ...extra
    )
  const chartTitleClass = clsx(
    classes.chartTitle,
    isClientDetailPane && classes.chartTitleClientDetail
  )
  const noDataClass = clsx(
    classes.noDataContainer,
    isClientDetailPane && classes.noDataContainerClientDetail
  )
  const showViraPath =
    client.users_user_feature_flags?.vira_path_enabled ?? false
  const showAssessments = pane === "all" && showViraPath
  const showActivityPatterns =
    (pane === "all" || pane === "activity") && showViraPath
  const showActivityChart = pane === "all" || pane === "activity"
  const showSleepingCharts = pane === "all" || pane === "sleep"
  const showLanguageChart = pane === "all" || pane === "language"
  /** Full Trends: Words Typed + Time at Home. Language pane: Words Typed only. Activity: Time at Home. */
  const showTimeAtHome = pane === "all" || pane === "activity"
  const showWordsTyped = pane === "all" || pane === "language"
  const activityChartsTitle =
    showWordsTyped && showTimeAtHome
      ? "Activity Charts"
      : showWordsTyped
      ? "Words Typed"
      : "Time at Home"
  const date =
    `${dayjs().subtract(14, "days").format("MMM DD")}-` +
    `${dayjs().subtract(1, "day").format("MMM DD")}`

  const [loading, setLoading] = useState(false)
  const [assessmentWeekOffset, setAssessmentWeekOffset] = useState(0)
  const myPlacesTrend = useMyPlacesTrend(
    client.id,
    showActivityPatterns || showAssessments
  )
  const earliestAssessmentDate = getEarliestAssessmentDate(
    client.mood_measurements
  )
  const maxAssessmentWeekOffset = earliestAssessmentDate
    ? Math.max(
        0,
        Math.floor(
          dayjs().startOf("day").diff(earliestAssessmentDate, "day") / 7
        )
      )
    : 0
  const canViewOlderAssessmentWeek =
    assessmentWeekOffset < maxAssessmentWeekOffset

  if (!trends) {
    return null
  }

  const activityChartsAverages = showWordsTyped
    ? showTimeAtHome
      ? [trends.activeSocialMedia, trends.timeAtHome]
      : [trends.activeSocialMedia]
    : [trends.timeAtHome]

  const exportToPdf = async () => {
    setLoading(true)
    await generatePdf(client.last_name || "", "Trends")
    setLoading(false)
  }

  return (
    <div className={classes.trends}>
      <div
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          zIndex: 5000,
        }}
        id="spinner"
      >
        <BounceLoader
          color={"rgb(117, 145, 228)"}
          size={75}
          loading={loading}
          aria-label="Loading Spinner"
          data-testid="loader"
        />
        <div style={{ display: loading ? "block" : "none" }}>
          Generating PDF
        </div>
      </div>

      {showAssessments && (
        <ChartAccordion
          variant={accordionVariant}
          title="Assessments"
          Chart={() => (
            <div className={classes.assessmentsContent}>
              <div className={classes.assessmentsHeader}>
                <div className={classes.assessmentsNavigation}>
                  <IconButton
                    aria-label="View older assessment week"
                    className={classes.assessmentsChevron}
                    disabled={!canViewOlderAssessmentWeek}
                    onClick={() => {
                      setAssessmentWeekOffset(
                        (currentOffset) => currentOffset + 1
                      )
                    }}
                  >
                    <ChevronLeftLargeIcon />
                  </IconButton>
                  <Typography
                    variant="h5"
                    className={classes.assessmentsWeekLabel}
                  >
                    {getAssessmentWeekLabel(assessmentWeekOffset)}
                  </Typography>
                  <IconButton
                    aria-label="View newer assessment week"
                    className={classes.assessmentsChevron}
                    disabled={assessmentWeekOffset === 0}
                    onClick={() => {
                      setAssessmentWeekOffset(
                        (currentOffset) => currentOffset - 1
                      )
                    }}
                  >
                    <ChevronRightIcon />
                  </IconButton>
                </div>
                {assessmentWeekOffset > 0 ? (
                  <Button
                    className={classes.returnToCurrentWeek}
                    onClick={() => setAssessmentWeekOffset(0)}
                  >
                    Return to This Week
                  </Button>
                ) : null}
              </div>
              <div className={classes.assessmentsCharts}>
                <div className={classes.barChartContainer}>
                  <CravingsChart
                    data={client.mood_measurements}
                    weekOffset={assessmentWeekOffset}
                  />
                </div>
                <div className={classes.barChartContainer}>
                  <EnjoymentChart
                    data={client.mood_measurements}
                    weekOffset={assessmentWeekOffset}
                  />
                </div>
              </div>
            </div>
          )}
        />
      )}

      {showActivityPatterns && (
        <ChartAccordion
          variant={accordionVariant}
          title="Activity Patterns"
          Chart={() => (
            <ContentCard title="My Places">
              <div className={classes.myPlacesChartContainer}>
                <MyPlacesChart {...myPlacesTrend} />
              </div>
            </ContentCard>
          )}
        />
      )}

      {showActivityChart && (
        <ChartAccordion
          variant={accordionVariant}
          title="Activity Chart"
          DailyAverages={() => (
            <WeeklyAverages
              data={[
                trends.walk,
                trends.exercise,
                trends.stationary,
                trends.driving,
              ]}
            />
          )}
          Chart={() => (
            <div className={classes.charts}>
              <ContentCard
                title="Walking, Exercise, and In Transit"
                titleStart={
                  <ActivityChartHelpIcon message="Minutes spent walking, exercising, and in transit each day." />
                }
                meta={
                  <ViraText tone="subtle" sx={{ fontSize: 12 }}>
                    {date}
                  </ViraText>
                }
                sx={{ mb: 2 }}
              >
                {activity && categories && (
                  <>
                    {activity.walking[0]?.length > 0 ||
                    activity.exercise[0]?.length > 0 ||
                    activity.driving[0]?.length > 0 ? (
                      <ActivitiesChart
                        activities={[
                          activity.walking,
                          activity.exercise,
                          activity.driving,
                        ]}
                        categories={categories}
                      />
                    ) : (
                      <Typography variant="body1" className={classes.noData}>
                        There is no data for{" "}
                        <strong>Walking, Exercise, and In Transit</strong>.
                        You’ll get to see the data once your client opts-in on
                        the mobile app.
                      </Typography>
                    )}
                  </>
                )}
              </ContentCard>
              <ContentCard
                title="Stationary"
                titleStart={
                  <ActivityChartHelpIcon message="Number of stationary minutes per day." />
                }
                meta={
                  <ViraText tone="subtle" sx={{ fontSize: 12 }}>
                    {date}
                  </ViraText>
                }
              >
                {activity && categories && (
                  <>
                    {activity.stationary[0]?.length > 0 ? (
                      <ActivitiesChart
                        activities={[activity.stationary]}
                        categories={categories}
                      />
                    ) : (
                      <Typography variant="body1" className={classes.noData}>
                        There is no data for <strong>Stationary</strong>. You’ll
                        get to see the data once your client opts-in on the
                        mobile app.
                      </Typography>
                    )}
                  </>
                )}
              </ContentCard>
            </div>
          )}
        />
      )}

      {showSleepingCharts && (
        <ChartAccordion
          variant={accordionVariant}
          title="Sleeping Charts"
          DailyAverages={() => (
            <WeeklyAverages
              data={[
                trends.bedtime,
                trends.waketime,
                trends.timeInBed,
                trends.morningness,
              ]}
            />
          )}
          Chart={() => (
            <div className={classes.charts}>
              <ContentCard
                title="Bedtime, Waketime, and Time in Bed"
                titleStart={<SleepChartHelpIcon />}
                meta={
                  <ViraText tone="subtle" sx={{ fontSize: 12 }}>
                    {date}
                  </ViraText>
                }
                sx={{ mb: 2 }}
              >
                {sleep && sleep.length > 0 ? (
                  <SleepChart sleep={sleep} />
                ) : (
                  <Typography variant="body1" className={classes.noData}>
                    There is no data for{" "}
                    <strong>Bedtime, Waketime, and Time in Bed</strong>. You’ll
                    get to see the data once your client opts-in on the mobile
                    app.
                  </Typography>
                )}
              </ContentCard>
              <ContentCard
                title="Morningness"
                titleStart={<MorningnessChartHelpIcon />}
                meta={
                  <ViraText tone="subtle" sx={{ fontSize: 12 }}>
                    {date}
                  </ViraText>
                }
              >
                {morningness && morningness[0]?.length > 0 && categories ? (
                  <MorningnessChart
                    data={morningness}
                    categories={categories}
                  />
                ) : (
                  <Typography variant="body1" className={classes.noData}>
                    There is no data for <strong>Morningness</strong>. You’ll
                    get to see the data once your client opts-in on the mobile
                    app.
                  </Typography>
                )}
              </ContentCard>
            </div>
          )}
        />
      )}

      {showLanguageChart && (
        <ChartAccordion
          variant={accordionVariant}
          title="Language Chart"
          DailyAverages={() => (
            <WeeklyAverages
              data={[
                trends.positiveWords,
                trends.negativeWords,
                trends.selfFocusWords,
                trends.rigidWords,
              ]}
            />
          )}
          Chart={() => (
            <ContentCard
              title="Positive, Negative, Self-Focus, and Rigid Words"
              titleStart={<LanguageChartHelpIcon />}
              meta={
                <ViraText tone="subtle" sx={{ fontSize: 12 }}>
                  {date}
                </ViraText>
              }
            >
              {language && categories && (
                <>
                  {language.positive[0]?.length > 0 &&
                  language.negative[0]?.length > 0 &&
                  language.selfFocus[0]?.length > 0 &&
                  language.rigid[0]?.length > 0 ? (
                    <LanguageChart
                      languageData={[
                        language.positive,
                        language.negative,
                        language.selfFocus,
                        language.rigid,
                      ]}
                      categories={categories}
                    />
                  ) : (
                    <Typography variant="body1" className={classes.noData}>
                      There is no data for{" "}
                      <strong>
                        Positive, Negative, Self-Focus, and Rigid Words
                      </strong>
                      . You’ll get to see the data once your client opts-in on
                      the mobile app.
                    </Typography>
                  )}
                </>
              )}
            </ContentCard>
          )}
        />
      )}

      {(showWordsTyped || showTimeAtHome) && (
        <ChartAccordion
          variant={accordionVariant}
          title={activityChartsTitle}
          DailyAverages={() => <WeeklyAverages data={activityChartsAverages} />}
          Chart={() => (
            <div className={classes.charts}>
              {showWordsTyped && (
                <ContentCard
                  title="Words Typed in Apps"
                  titleStart={<SocialMediaChartHelpIcon />}
                  meta={
                    <ViraText tone="subtle" sx={{ fontSize: 12 }}>
                      {date}
                    </ViraText>
                  }
                  sx={{ mb: showTimeAtHome ? 2 : 0 }}
                >
                  {socialMedia && socialMedia[0]?.length > 0 && categories ? (
                    <SocialMediaChart
                      data={socialMedia}
                      categories={categories}
                    />
                  ) : (
                    <Typography variant="body1" className={classes.noData}>
                      There is no data for <strong>Words Typed in Apps</strong>.
                      You’ll get to see the data once your client opts-in on the
                      mobile app.
                    </Typography>
                  )}
                </ContentCard>
              )}
              {showTimeAtHome && (
                <ContentCard
                  title="Time at Home"
                  titleStart={<TimeAtHomeChartHelpIcon />}
                  meta={
                    <ViraText tone="subtle" sx={{ fontSize: 12 }}>
                      {date}
                    </ViraText>
                  }
                >
                  {timeAtHome && timeAtHome[0]?.length > 0 && categories ? (
                    <TimeAtHomeChart
                      data={timeAtHome}
                      categories={categories}
                    />
                  ) : (
                    <Typography variant="body1" className={classes.noData}>
                      There is no data for <strong>Time at Home</strong>. You’ll
                      get to see the data once your client opts-in on the mobile
                      app.
                    </Typography>
                  )}
                </ContentCard>
              )}
            </div>
          )}
        />
      )}
      {!isClientDetailPane ? (
        <Grid
          container
          direction="row"
          justifyContent="center"
          alignItems="center"
        >
          <Button
            variant="outlined"
            onClick={exportToPdf}
            style={{ textTransform: "none" }}
            disabled={loading}
          >
            Export
          </Button>
        </Grid>
      ) : null}
    </div>
  )
}

export default Trends
